import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Bot, Cpu, Cog, Zap, ArrowLeft, ArrowRight, Play, 
  CheckCircle2, Star, Lightbulb, Code, Gamepad2,
  Radio, Battery, CircuitBoard, Settings, Wrench,
  Leaf, Rocket, Volume2, VolumeX, Sparkles
} from 'lucide-react';

const RoboticsLabPage = () => {
  const navigate = useNavigate();
  const [activeLesson, setActiveLesson] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [orbPulse, setOrbPulse] = useState(false);

  // Orb pulse effect
  useEffect(() => {
    const interval = setInterval(() => {
      setOrbPulse(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Science modules for navigation
  const scienceModules = [
    { id: 'circuits', title: 'Circuit Lab', icon: Zap, route: '/science/circuits', color: '#f59e0b' },
    { id: 'biology', title: 'Biology Lab', icon: Leaf, route: '/science/biology', color: '#10b981' },
    { id: 'physics', title: 'Force & Motion', icon: Rocket, route: '/science/physics', color: '#06b6d4' },
    { id: 'machines', title: 'Simple Machines', icon: Cog, route: '/science/machines', color: '#a855f7' },
    { id: 'robotics', title: 'Robotics', icon: Bot, route: '/science/robotics', color: '#00f0ff' }
  ];

  // Robotics Lessons with neon colors
  const lessons = [
    {
      id: 'intro',
      title: 'What is a Robot?',
      titleHindi: 'रोबोट क्या है?',
      icon: Bot,
      color: 'from-cyan-500 to-blue-600',
      glowColor: '#00f0ff',
      description: 'Learn what makes a machine a robot!',
      content: {
        definition: 'A robot is a machine that can sense, think, and act!',
        parts: ['Sensors (Eyes & Ears)', 'Processor (Brain)', 'Actuators (Hands & Legs)', 'Power Source (Heart)'],
        funFact: 'The word "Robot" comes from Czech word "robota" meaning forced labor!'
      }
    },
    {
      id: 'parts',
      title: 'Robot Body Parts',
      titleHindi: 'रोबोट के अंग',
      icon: Cog,
      color: 'from-violet-500 to-purple-600',
      glowColor: '#a855f7',
      description: 'Discover the key components!',
      content: {
        parts: [
          { name: 'Sensors', icon: Radio, desc: 'Detect light, sound, touch, distance', hindi: 'सेंसर - महसूस करते हैं' },
          { name: 'Microcontroller', icon: Cpu, desc: 'The brain - Arduino, Raspberry Pi', hindi: 'दिमाग - सोचता है' },
          { name: 'Motors', icon: Cog, desc: 'Make things move - wheels, arms', hindi: 'मोटर - चलाते हैं' },
          { name: 'Battery', icon: Battery, desc: 'Power source - gives energy', hindi: 'बैटरी - ऊर्जा देती है' }
        ]
      }
    },
    {
      id: 'types',
      title: 'Types of Robots',
      titleHindi: 'रोबोट के प्रकार',
      icon: Gamepad2,
      color: 'from-emerald-500 to-teal-600',
      glowColor: '#10b981',
      description: 'So many cool robots exist!',
      content: {
        types: [
          { name: 'Industrial Robots', desc: 'Factory mein kaam karte hain', emoji: '🏭' },
          { name: 'Medical Robots', desc: 'Surgery mein help karte hain', emoji: '🏥' },
          { name: 'Space Robots', desc: 'Mars pe explore karte hain', emoji: '🚀' },
          { name: 'Home Robots', desc: 'Ghar ki safai karte hain', emoji: '🏠' },
          { name: 'Educational Robots', desc: 'Coding sikhate hain', emoji: '📚' }
        ]
      }
    },
    {
      id: 'build',
      title: 'Build Your First Robot',
      titleHindi: 'अपना पहला रोबोट बनाओ',
      icon: Wrench,
      color: 'from-cyan-400 to-blue-500',
      glowColor: '#00f0ff',
      description: 'Step-by-step guide to build!',
      content: {
        steps: [
          { step: 1, title: 'Gather Materials', desc: 'Motor, battery, cardboard, wires', emoji: '📦' },
          { step: 2, title: 'Build the Body', desc: 'Cut cardboard for chassis', emoji: '✂️' },
          { step: 3, title: 'Attach Motors', desc: 'Connect wheels to motors', emoji: '⚙️' },
          { step: 4, title: 'Wire the Circuit', desc: 'Connect battery to motor', emoji: '🔌' },
          { step: 5, title: 'Test & Play!', desc: 'Switch on and watch it move!', emoji: '🎉' }
        ]
      }
    },
    {
      id: 'coding',
      title: 'Robot Programming',
      titleHindi: 'रोबोट प्रोग्रामिंग',
      icon: Code,
      color: 'from-blue-500 to-indigo-600',
      glowColor: '#3b82f6',
      description: 'Tell your robot what to do!',
      content: {
        intro: 'Programming is giving instructions to robots - just like a recipe!',
        commands: [
          { cmd: 'MOVE FORWARD', desc: 'Aage badho', icon: '⬆️' },
          { cmd: 'TURN LEFT', desc: 'Baayein mudo', icon: '⬅️' },
          { cmd: 'TURN RIGHT', desc: 'Daayein mudo', icon: '➡️' },
          { cmd: 'STOP', desc: 'Ruko', icon: '🛑' },
          { cmd: 'REPEAT', desc: 'Dobara karo', icon: '🔄' }
        ],
        languages: ['Scratch', 'Blockly', 'Python', 'Arduino']
      }
    }
  ];

  const markComplete = (lessonId) => {
    if (!completedLessons.includes(lessonId)) {
      setCompletedLessons([...completedLessons, lessonId]);
    }
  };

  return (
    <div className="min-h-screen bg-[#030810] relative overflow-hidden">
      {/* Animated Cyber Grid Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px'
        }}></div>
        {/* Scan line effect */}
        <div className="absolute inset-0 opacity-20" style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 240, 255, 0.03) 2px, rgba(0, 240, 255, 0.03) 4px)'
        }}></div>
      </div>

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
              backgroundColor: ['#00f0ff', '#00ff88', '#a855f7', '#3b82f6'][i % 4],
              opacity: 0.4 + Math.random() * 0.3,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${4 + Math.random() * 6}s`,
              boxShadow: `0 0 ${10 + Math.random() * 10}px currentColor`
            }}
          ></div>
        ))}
      </div>

      {/* Gradient Orbs in Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[150px]"></div>
      </div>
      {/* Header with Navigation */}
      <header className="sticky top-0 z-50 bg-[#030810]/80 backdrop-blur-xl border-b border-cyan-500/20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/science')}
                className="p-2 hover:bg-cyan-500/10 rounded-lg transition-colors border border-transparent hover:border-cyan-500/30"
                data-testid="robotics-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-cyan-400" />
              </button>
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/40">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -inset-1 bg-cyan-500/20 rounded-xl blur-md -z-10"></div>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white flex items-center gap-2">
                  Robotics Lab
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs animate-pulse">NEW</Badge>
                </h1>
              </div>
            </div>
            
            {/* Module Navigation Tabs */}
            <div className="hidden lg:flex items-center gap-1 bg-cyan-500/5 rounded-full p-1 border border-cyan-500/20">
              {scienceModules.map((mod) => (
                <button
                  key={mod.id}
                  onClick={() => navigate(mod.route)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all ${
                    mod.id === 'robotics' 
                      ? 'bg-cyan-500/20 text-cyan-400 font-medium shadow-lg shadow-cyan-500/20' 
                      : 'text-gray-400 hover:bg-cyan-500/10 hover:text-cyan-300'
                  }`}
                >
                  <mod.icon className="w-3.5 h-3.5" style={{ color: mod.id === 'robotics' ? '#00f0ff' : mod.color }} />
                  <span>{mod.title}</span>
                </button>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-2 hover:bg-cyan-500/10 rounded-lg transition-colors"
              >
                {soundEnabled ? <Volume2 className="w-5 h-5 text-cyan-400" /> : <VolumeX className="w-5 h-5 text-gray-600" />}
              </button>
              <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30">Class 6-10</Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Glowing Orb */}
      <section className="py-16 px-4 relative">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          
          {/* Central Glowing Orb */}
          <div className="relative inline-block mb-10">
            {/* Outer glow rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`w-56 h-56 rounded-full border-2 border-cyan-500/30 ${orbPulse ? 'scale-110 opacity-50' : 'scale-100 opacity-30'} transition-all duration-1000`}></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`w-48 h-48 rounded-full border border-cyan-400/20 ${orbPulse ? 'scale-105 opacity-40' : 'scale-100 opacity-20'} transition-all duration-1000`}></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-64 rounded-full bg-cyan-500/5 blur-xl animate-pulse"></div>
            </div>
            
            {/* Main Orb */}
            <div className="relative w-40 h-40 mx-auto">
              {/* Orb outer ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/50 via-blue-500/50 to-purple-500/50 animate-spin-slow" style={{ animationDuration: '8s' }}></div>
              
              {/* Orb core */}
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-[#0a1628] to-[#051020] flex items-center justify-center overflow-hidden">
                {/* Inner glow */}
                <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/20 via-transparent to-transparent"></div>
                
                {/* Robot icon */}
                <Bot className="w-16 h-16 text-cyan-400 relative z-10 animate-pulse" />
                
                {/* Reflection effect */}
                <div className="absolute top-2 left-1/4 w-8 h-8 bg-white/10 rounded-full blur-md"></div>
              </div>
              
              {/* Orbiting particles */}
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: '6s' }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50"></div>
              </div>
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: '8s', animationDirection: 'reverse' }}>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-400 rounded-full shadow-lg shadow-blue-400/50"></div>
              </div>
              <div className="absolute inset-0 animate-spin-slow" style={{ animationDuration: '10s' }}>
                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-2 h-2 bg-purple-400 rounded-full shadow-lg shadow-purple-400/50"></div>
              </div>
              
              {/* Glow shadow */}
              <div className="absolute inset-0 rounded-full shadow-2xl shadow-cyan-500/40"></div>
            </div>
            
            {/* Floating circuit elements */}
            <div className="absolute -top-4 -right-8 w-12 h-12 border border-cyan-500/30 rounded-lg flex items-center justify-center animate-float" style={{ animationDelay: '0s' }}>
              <Cpu className="w-6 h-6 text-cyan-400" />
            </div>
            <div className="absolute -bottom-4 -left-8 w-12 h-12 border border-blue-500/30 rounded-lg flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
              <CircuitBoard className="w-6 h-6 text-blue-400" />
            </div>
            <div className="absolute top-1/2 -right-12 -translate-y-1/2 w-10 h-10 border border-purple-500/30 rounded-full flex items-center justify-center animate-float" style={{ animationDelay: '2s' }}>
              <Cog className="w-5 h-5 text-purple-400 animate-spin-slow" style={{ animationDuration: '4s' }} />
            </div>
          </div>

          {/* Title with Neon Effect */}
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 animate-gradient-x">Create Robots!</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-2">
            <span className="text-cyan-400">Dosto!</span> Aaj hum seekhenge robots kaise bante hain!
          </p>
          <p className="text-gray-500 max-w-xl mx-auto">
            From basic parts to programming - <span className="text-cyan-400">full robotics journey!</span>
          </p>

          {/* Progress with Neon Style */}
          <div className="flex items-center justify-center gap-2 mt-8">
            <div className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500/10 rounded-full border border-cyan-500/30 shadow-lg shadow-cyan-500/10">
              <Star className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300">{completedLessons.length}/{lessons.length} Lessons Complete</span>
            </div>
          </div>
        </div>
      </section>

      {/* Lessons Grid with Neon Cards */}
      <section className="py-8 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-cyan-400" />
            Learning Modules
            <span className="text-gray-500 font-normal text-base ml-2">(सीखने के पाठ)</span>
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lessons.map((lesson, idx) => (
              <Card 
                key={lesson.id}
                data-testid={`lesson-card-${lesson.id}`}
                className={`relative overflow-hidden bg-[#0a1628]/80 border border-cyan-500/20 hover:border-cyan-400/50 cursor-pointer group transition-all duration-500 hover:scale-105 backdrop-blur-sm ${
                  completedLessons.includes(lesson.id) ? 'ring-2 ring-cyan-500/50' : ''
                }`}
                style={{
                  boxShadow: 'inset 0 1px 0 0 rgba(0, 240, 255, 0.1)'
                }}
                onClick={() => setActiveLesson(lesson)}
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  background: `radial-gradient(circle at center, ${lesson.glowColor}15, transparent 70%)`
                }}></div>
                
                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-24 h-24 opacity-20 group-hover:opacity-40 transition-opacity">
                  <div className="absolute top-0 right-0 w-full h-full" style={{
                    background: `linear-gradient(225deg, ${lesson.glowColor}40, transparent 70%)`
                  }}></div>
                </div>
                
                <CardContent className="p-6 relative z-10">
                  {/* Completed badge */}
                  {completedLessons.includes(lesson.id) && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle2 className="w-6 h-6 text-cyan-400 drop-shadow-[0_0_8px_rgba(0,240,255,0.5)]" />
                    </div>
                  )}
                  
                  {/* Number with glow */}
                  <div className="absolute top-4 right-4 text-4xl font-bold text-cyan-500/10">
                    0{idx + 1}
                  </div>
                  
                  {/* Icon with neon glow */}
                  <div 
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${lesson.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 relative`}
                  >
                    <lesson.icon className="w-7 h-7 text-white relative z-10" />
                    <div className="absolute inset-0 rounded-xl blur-md opacity-50 group-hover:opacity-80 transition-opacity" style={{ backgroundColor: lesson.glowColor }}></div>
                  </div>
                  
                  {/* Content */}
                  <h4 className="text-lg font-bold text-white mb-1 group-hover:text-cyan-300 transition-colors">{lesson.title}</h4>
                  <p className="text-sm text-gray-500 mb-2">{lesson.titleHindi}</p>
                  <p className="text-sm text-gray-400">{lesson.description}</p>
                  
                  {/* Explore indicator */}
                  <div className="mt-4 flex items-center gap-1 text-cyan-400 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                    <span className="text-xs">Explore</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </CardContent>
                
                {/* Bottom neon line */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Robot Builder Preview - Futuristic */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#0a1628]/90 to-[#051020]/90 rounded-3xl border border-cyan-500/20 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(0, 240, 255, 0.3) 0%, transparent 50%),
                               radial-gradient(circle at 80% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)`
            }}></div>
            
            <div className="grid md:grid-cols-2 gap-8 items-center p-8 relative z-10">
              {/* Robot Diagram */}
              <div className="relative">
                <RobotDiagram />
              </div>
              
              {/* Info */}
              <div>
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 mb-4">Interactive</Badge>
                <h3 className="text-2xl font-bold text-white mb-3">
                  Robot Anatomy - <span className="text-cyan-400">जानो रोबोट को!</span>
                </h3>
                <p className="text-gray-400 mb-6">
                  Har robot ke 4 main parts hote hain - just like us! 
                  <span className="text-cyan-400"> Sensors</span> se feel karta hai, 
                  <span className="text-blue-400"> Brain</span> se sochta hai, 
                  <span className="text-emerald-400"> Motors</span> se move karta hai!
                </p>
                <div className="space-y-3">
                  {[
                    { icon: Radio, name: 'Sensors', desc: 'Input - Duniya ko dekhta hai', color: 'cyan', glowColor: '#00f0ff' },
                    { icon: Cpu, name: 'Processor', desc: 'Think - Decision leta hai', color: 'blue', glowColor: '#3b82f6' },
                    { icon: Cog, name: 'Actuators', desc: 'Output - Action karta hai', color: 'emerald', glowColor: '#10b981' },
                    { icon: Battery, name: 'Power', desc: 'Energy - Chalta rehta hai', color: 'purple', glowColor: '#a855f7' }
                  ].map((item, i) => (
                    <div 
                      key={i} 
                      className="flex items-center gap-3 p-3 bg-[#0a1628]/50 rounded-xl border border-cyan-500/10 hover:border-cyan-500/30 transition-all group cursor-pointer"
                    >
                      <div className="relative">
                        <item.icon className={`w-5 h-5 text-${item.color}-400`} style={{ color: item.glowColor }} />
                        <div className="absolute inset-0 blur-md opacity-50" style={{ backgroundColor: item.glowColor }}></div>
                      </div>
                      <div>
                        <span className="text-white font-medium group-hover:text-cyan-300 transition-colors">{item.name}</span>
                        <span className="text-gray-500 text-sm ml-2">- {item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fun Facts with Neon Style */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-xl font-bold text-white mb-6 text-center flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            Amazing Robot Facts!
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { fact: 'First robot was made in 1954!', emoji: '📅', color: '#00f0ff' },
              { fact: 'Sophia robot has citizenship of Saudi Arabia', emoji: '🤖', color: '#a855f7' },
              { fact: 'Mars Rover is a robot on another planet!', emoji: '🚀', color: '#10b981' },
              { fact: 'Japan has robot hotels with robot staff', emoji: '🏨', color: '#3b82f6' }
            ].map((item, i) => (
              <div 
                key={i} 
                className="flex items-center gap-3 p-4 bg-[#0a1628]/80 rounded-xl border border-cyan-500/20 hover:border-cyan-400/40 transition-all group cursor-pointer backdrop-blur-sm"
                style={{
                  boxShadow: `inset 0 0 30px ${item.color}08`
                }}
              >
                <span className="text-2xl">{item.emoji}</span>
                <p className="text-gray-300 group-hover:text-white transition-colors">{item.fact}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lesson Modal */}
      {activeLesson && (
        <LessonModal 
          lesson={activeLesson} 
          onClose={() => setActiveLesson(null)}
          onComplete={() => {
            markComplete(activeLesson.id);
            setActiveLesson(null);
          }}
        />
      )}

      {/* Footer with Neon Border */}
      <footer className="py-8 px-4 border-t border-cyan-500/20 relative z-10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
            <Bot className="w-4 h-4 text-cyan-500" />
            Practiq Robotics Lab - Building Future Innovators! | Class 6-10
          </p>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes float-particle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.8;
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        .animate-float-particle {
          animation: float-particle 5s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

// Robot Diagram Component - Futuristic Neon Style
const RobotDiagram = () => {
  const [hoveredPart, setHoveredPart] = useState(null);
  
  return (
    <div className="relative bg-[#030810]/80 rounded-3xl p-8 border border-cyan-500/20 backdrop-blur-sm overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.1) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(0, 240, 255, 0.1) 1px, transparent 1px)`,
        backgroundSize: '20px 20px'
      }}></div>
      
      {/* Robot Body */}
      <div className="relative w-48 h-64 mx-auto">
        {/* Head - Brain/Processor */}
        <div 
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center cursor-pointer transition-all duration-300 ${hoveredPart === 'brain' ? 'scale-110' : ''}`}
          style={{
            boxShadow: hoveredPart === 'brain' ? '0 0 40px rgba(0, 240, 255, 0.5), inset 0 0 20px rgba(0, 240, 255, 0.3)' : '0 0 20px rgba(0, 240, 255, 0.2)'
          }}
          onMouseEnter={() => setHoveredPart('brain')}
          onMouseLeave={() => setHoveredPart(null)}
        >
          <Cpu className="w-10 h-10 text-white" />
          {hoveredPart === 'brain' && (
            <div className="absolute -top-14 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-cyan-500 rounded-lg text-xs text-white whitespace-nowrap shadow-lg shadow-cyan-500/50 z-20">
              Brain (Processor)
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-cyan-500 rotate-45"></div>
            </div>
          )}
        </div>
        
        {/* Eyes (Sensors) */}
        <div 
          className={`absolute top-6 left-1/2 -translate-x-1/2 flex gap-2 cursor-pointer transition-all duration-300 ${hoveredPart === 'sensors' ? 'scale-110' : ''}`}
          onMouseEnter={() => setHoveredPart('sensors')}
          onMouseLeave={() => setHoveredPart(null)}
        >
          <div className="w-4 h-4 bg-cyan-400 rounded-full animate-pulse" style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.8)' }}></div>
          <div className="w-4 h-4 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s', boxShadow: '0 0 15px rgba(0, 240, 255, 0.8)' }}></div>
          {hoveredPart === 'sensors' && (
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-cyan-400 rounded-lg text-xs text-black whitespace-nowrap shadow-lg shadow-cyan-400/50 z-20">
              Sensors (Eyes)
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-cyan-400 rotate-45"></div>
            </div>
          )}
        </div>
        
        {/* Body */}
        <div className="absolute top-24 left-1/2 -translate-x-1/2 w-28 h-28 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl border border-cyan-500/20"
          style={{ boxShadow: 'inset 0 0 30px rgba(0, 240, 255, 0.1)' }}
        ></div>
        
        {/* Battery/Power in body */}
        <div 
          className={`absolute top-28 left-1/2 -translate-x-1/2 w-16 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center cursor-pointer transition-all duration-300 ${hoveredPart === 'power' ? 'scale-110' : ''}`}
          style={{
            boxShadow: hoveredPart === 'power' ? '0 0 30px rgba(168, 85, 247, 0.6)' : '0 0 15px rgba(168, 85, 247, 0.3)'
          }}
          onMouseEnter={() => setHoveredPart('power')}
          onMouseLeave={() => setHoveredPart(null)}
        >
          <Battery className="w-8 h-8 text-white" />
          {hoveredPart === 'power' && (
            <div className="absolute -right-28 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-purple-500 rounded-lg text-xs text-white whitespace-nowrap shadow-lg shadow-purple-500/50 z-20">
              Power Source
              <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1 w-2 h-2 bg-purple-500 rotate-45"></div>
            </div>
          )}
        </div>
        
        {/* Arms (Actuators/Motors) */}
        <div 
          className={`absolute top-28 -left-4 w-8 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg cursor-pointer transition-all duration-300 ${hoveredPart === 'actuators' ? 'scale-110' : ''}`}
          style={{
            boxShadow: hoveredPart === 'actuators' ? '0 0 25px rgba(16, 185, 129, 0.6)' : '0 0 10px rgba(16, 185, 129, 0.3)'
          }}
          onMouseEnter={() => setHoveredPart('actuators')}
          onMouseLeave={() => setHoveredPart(null)}
        >
          {hoveredPart === 'actuators' && (
            <div className="absolute -left-20 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-emerald-500 rounded-lg text-xs text-white whitespace-nowrap shadow-lg shadow-emerald-500/50 z-20">
              Motors
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-emerald-500 rotate-45"></div>
            </div>
          )}
        </div>
        <div 
          className={`absolute top-28 -right-4 w-8 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg transition-all duration-300 ${hoveredPart === 'actuators' ? 'scale-110' : ''}`}
          style={{
            boxShadow: hoveredPart === 'actuators' ? '0 0 25px rgba(16, 185, 129, 0.6)' : '0 0 10px rgba(16, 185, 129, 0.3)'
          }}
        ></div>
        
        {/* Wheels with neon rings */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-8">
          <div className="relative">
            <div className="w-10 h-10 bg-slate-800 rounded-full border-2 border-cyan-500/50" style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)' }}></div>
            <div className="absolute inset-1 rounded-full border border-cyan-500/30"></div>
          </div>
          <div className="relative">
            <div className="w-10 h-10 bg-slate-800 rounded-full border-2 border-cyan-500/50" style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)' }}></div>
            <div className="absolute inset-1 rounded-full border border-cyan-500/30"></div>
          </div>
        </div>
        
        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
          <line x1="50%" y1="20%" x2="50%" y2="37%" stroke="rgba(0, 240, 255, 0.3)" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>
      
      <p className="text-center text-cyan-400/70 text-sm mt-6">Hover over parts to learn!</p>
    </div>
  );
};

// Lesson Modal - Futuristic Neon Style
const LessonModal = ({ lesson, onClose, onComplete }) => {
  const [step, setStep] = useState(0);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#030810]/90 backdrop-blur-md">
      <div className="bg-[#0a1628] rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl border border-cyan-500/30"
        style={{ boxShadow: '0 0 60px rgba(0, 240, 255, 0.15), inset 0 1px 0 rgba(0, 240, 255, 0.1)' }}
      >
        {/* Header with gradient */}
        <div className={`p-5 bg-gradient-to-r ${lesson.color} relative overflow-hidden`}>
          {/* Scan line effect */}
          <div className="absolute inset-0 opacity-20" style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)'
          }}></div>
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <lesson.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{lesson.title}</h3>
                <p className="text-sm text-white/80">{lesson.titleHindi}</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/20 rounded-lg text-white transition-colors"
              data-testid="lesson-modal-close"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh] custom-scrollbar">
          {lesson.id === 'intro' && (
            <div className="space-y-4">
              <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/30">
                <p className="text-lg text-cyan-400 font-medium">{lesson.content.definition}</p>
              </div>
              <h4 className="text-white font-bold">Robot ke 4 main parts:</h4>
              <div className="grid grid-cols-2 gap-3">
                {lesson.content.parts.map((part, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-[#051020] rounded-lg border border-cyan-500/20 hover:border-cyan-500/40 transition-colors">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                    <span className="text-gray-300 text-sm">{part}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-purple-500/10 rounded-xl border border-purple-500/30">
                <p className="text-sm text-purple-300">💡 Fun Fact: {lesson.content.funFact}</p>
              </div>
            </div>
          )}
          
          {lesson.id === 'parts' && (
            <div className="space-y-4">
              {lesson.content.parts.map((part, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-[#051020] rounded-xl border border-cyan-500/20 hover:border-cyan-500/40 transition-all group">
                  <div className={`w-12 h-12 bg-gradient-to-br ${lesson.color} rounded-xl flex items-center justify-center shadow-lg`} style={{ boxShadow: `0 0 20px ${lesson.glowColor}40` }}>
                    <part.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold group-hover:text-cyan-300 transition-colors">{part.name}</h4>
                    <p className="text-gray-400 text-sm">{part.desc}</p>
                    <p className="text-cyan-400/70 text-xs mt-1">{part.hindi}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {lesson.id === 'types' && (
            <div className="space-y-3">
              {lesson.content.types.map((type, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-[#051020] rounded-xl border border-cyan-500/20 hover:border-cyan-500/40 transition-all group">
                  <span className="text-3xl">{type.emoji}</span>
                  <div>
                    <h4 className="text-white font-bold group-hover:text-cyan-300 transition-colors">{type.name}</h4>
                    <p className="text-gray-400 text-sm">{type.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {lesson.id === 'build' && (
            <div className="space-y-3">
              {lesson.content.steps.map((s, i) => (
                <div key={i} className="flex items-start gap-4 p-4 bg-[#051020] rounded-xl border border-cyan-500/20 hover:border-cyan-500/40 transition-all group">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg" style={{ boxShadow: '0 0 15px rgba(0, 240, 255, 0.4)' }}>
                    {s.step}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-white font-bold flex items-center gap-2 group-hover:text-cyan-300 transition-colors">
                      {s.title} <span className="text-xl">{s.emoji}</span>
                    </h4>
                    <p className="text-gray-400 text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {lesson.id === 'coding' && (
            <div className="space-y-4">
              <p className="text-gray-300">{lesson.content.intro}</p>
              <h4 className="text-white font-bold">Basic Commands:</h4>
              <div className="grid grid-cols-2 gap-2">
                {lesson.content.commands.map((cmd, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 bg-[#051020] rounded-lg border border-cyan-500/20 hover:border-cyan-500/40 transition-colors">
                    <span className="text-xl">{cmd.icon}</span>
                    <div>
                      <span className="text-cyan-400 font-mono text-sm">{cmd.cmd}</span>
                      <p className="text-gray-500 text-xs">{cmd.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <h4 className="text-white font-bold mt-4">Programming Languages:</h4>
              <div className="flex flex-wrap gap-2">
                {lesson.content.languages.map((lang, i) => (
                  <span key={i} className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full text-sm border border-cyan-500/30">
                    {lang}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-cyan-500/20 flex justify-between bg-[#051020]/50">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="border-cyan-500/30 text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-300"
            data-testid="lesson-close-btn"
          >
            Close
          </Button>
          <Button 
            onClick={onComplete} 
            className={`bg-gradient-to-r ${lesson.color} hover:opacity-90 shadow-lg`}
            style={{ boxShadow: `0 0 20px ${lesson.glowColor}40` }}
            data-testid="lesson-complete-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Mark Complete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RoboticsLabPage;
